import { ApplicationFieldRow } from './applicationDisplay';
import { abbreviateLastName } from './personName';

export type ExtractedApplication = {
    phone: string;
    summary: string;
    fields: ApplicationFieldRow[];
};

type Question = {
    id: string;
    heading: string;
    match: RegExp;
    skip?: boolean;
};

const QUESTIONS: Question[] = [
    { id: 'firstName', heading: 'First name', match: /first name/i },
    { id: 'lastName', heading: 'Last name', match: /last name/i },
    { id: 'email', heading: 'Email', match: /^e-?mail$/i },
    { id: 'street', heading: 'Street', match: /street address|addr_line|address line/i, skip: true },
    { id: 'city', heading: 'City', match: /^city$/i },
    { id: 'state', heading: 'State', match: /state\s*\/\s*province|^state$/i },
    { id: 'zip', heading: 'ZIP', match: /postal\s*\/\s*zip|^country$/i, skip: true },
    { id: 'phone', heading: 'Phone', match: /phone number|area code/i },
    { id: 'color1', heading: 'First color', match: /first color preference/i },
    { id: 'gender1', heading: 'First gender', match: /first gender preference/i },
    { id: 'color2', heading: 'Second color', match: /second color preference/i },
    { id: 'gender2', heading: 'Second gender', match: /second gender preference/i },
    {
        id: 'flexPrefs',
        heading: 'Open to other prefs',
        match: /willing to take a puppy that does not match/i,
    },
    {
        id: 'olderDog',
        heading: 'Older puppy / adult',
        match: /open to an older puppy or adult/i,
    },
    { id: 'why', heading: 'Why a Standard Poodle', match: /why are you interested/i },
    { id: 'firstDog', heading: 'First dog', match: /is this your first dog/i },
    { id: 'firstPuppy', heading: 'First puppy', match: /first time raising a puppy/i },
    { id: 'otherPetsYes', heading: 'Other pets', match: /do you have other pets/i },
    {
        id: 'otherPets',
        heading: 'Other pets',
        match: /list species breed and age|enter n\/a if none/i,
    },
    { id: 'hasVet', heading: 'Veterinarian', match: /already have a veterinarian/i, skip: true },
    { id: 'vetName', heading: 'Veterinarian', match: /^if yes, who/i, skip: true },
    { id: 'grooming', heading: 'Grooming', match: /plans for grooming/i },
    { id: 'plans', heading: 'Plans for the dog', match: /what do you plan on doing with your dog/i },
    { id: 'housingType', heading: 'Housing', match: /type of housing/i },
    { id: 'ownRent', heading: 'Own / rent', match: /^do you$/i },
    { id: 'housingOther', heading: 'Housing notes', match: /if other, please explain/i },
    { id: 'landlord', heading: 'Landlord allows pets', match: /landlord allow pets/i },
    { id: 'yard', heading: 'Yard', match: /fenced yard/i },
    { id: 'fence', heading: 'Fence', match: /how high is the fence/i },
    { id: 'adultsAgree', heading: 'Adults agree', match: /adults in the home in agreement/i },
    { id: 'ages', heading: 'Household ages', match: /age of all adults and children/i },
    { id: 'allergies', heading: 'Allergies', match: /allergies to dogs/i },
    { id: 'whereTime', heading: 'Where the puppy spends time', match: /spend most of its time/i },
    { id: 'training', heading: 'Training', match: /how will the puppy be trained/i },
    { id: 'crate', heading: 'Crate train', match: /willing to crate train/i },
    { id: 'indoor', heading: 'Indoor / outdoor', match: /inside or outside dog/i },
    { id: 'hoursAlone', heading: 'Hours alone', match: /hours will the puppy.*alone/i },
    { id: 'gift', heading: 'Gift / surprise', match: /purchased as a surprise or gift/i },
    { id: 'cruelty', heading: 'Animal cruelty', match: /animal cruelty or animal hoarding/i, skip: true },
    { id: 'howSoon', heading: 'How soon', match: /how soon do you want to bring a puppy home/i },
    { id: 'pickupAge', heading: 'Pickup at 9–10 weeks', match: /pick the dog up at this age/i },
    { id: 'austin', heading: 'Austin pickup', match: /pick up your puppy in person in austin/i },
    { id: 'commitment', heading: 'Long-term commitment', match: /committed to unconditionally/i },
    { id: 'followUp', heading: 'Follow-up reports', match: /follow-up reports and photos/i },
    { id: 'rehome', heading: 'Contact us to rehome', match: /contact us first in the event/i },
    { id: 'understood', heading: 'Read the terms', match: /i've read this and i/i },
    { id: 'otherInfo', heading: 'Other info', match: /any other relevant information/i },
    { id: 'heard', heading: 'How they heard', match: /how did you hear about us/i },
    { id: 'lgbtq', heading: 'LGBTQ+', match: /lgtbq|lgbtq/i, skip: true },
    { id: 'questions', heading: 'Questions for us', match: /other questions for us/i },
    { id: 'signature', heading: 'Signature', match: /your signature/i, skip: true },
];

const NOISE =
    /please select|n\/a|not applicable|draw the image|submit|should be empty|afghanistan|united states/i;

const cleanValue = (value: string): string =>
    value
        .replace(/\s+/g, ' ')
        .replace(/^[:.\-\s]+/, '')
        .trim();

const formatPhone = (value: string): string => {
    const digits = value.replace(/\D/g, '');
    if (digits.length === 10) {
        return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    if (digits.length === 11 && digits.startsWith('1')) {
        return formatPhone(digits.slice(1));
    }
    return cleanValue(value);
};

const headingForLine = (line: string): Question | undefined => {
    const compact = line.replace(/[:?]+$/, '').trim();
    if (compact.length > 180) {
        return undefined;
    }
    return QUESTIONS.find((question) => question.match.test(compact));
};

export const extractApplicationFields = (text: string): Record<string, string> => {
    const lines = text
        .replace(/\r/g, '\n')
        .split('\n')
        .map((line) => line.replace(/[ \t]+/g, ' ').trim())
        .filter(Boolean);

    const fields: Record<string, string> = {};
    let current: Question | undefined;

    const flush = (question: Question | undefined, collected: string[]) => {
        if (!question || question.skip) {
            return;
        }
        const value = cleanValue(collected.join(' '));
        if (!value || (NOISE.test(value) && value.length < 24)) {
            return;
        }
        fields[question.id] = question.id === 'phone' ? formatPhone(value) : value;
    };

    let collected: string[] = [];
    lines.forEach((line) => {
        const heading = headingForLine(line);
        if (heading) {
            flush(current, collected);
            current = heading;
            collected = [];
            return;
        }
        if (current) {
            collected.push(line);
        }
    });
    flush(current, collected);
    return fields;
};

const row = (label: string, value?: string): ApplicationFieldRow | null => {
    const trimmed = value ? cleanValue(value) : '';
    return trimmed ? { label, value: trimmed } : null;
};

export const applicationFieldRows = (fields: Record<string, string>): ApplicationFieldRow[] => {
    const name = abbreviateLastName([fields.firstName, fields.lastName].filter(Boolean).join(' '));
    const location = [fields.city, fields.state].filter(Boolean).join(', ');
    const prefs = [
        [fields.color1, fields.gender1].filter(Boolean).join(' '),
        [fields.color2, fields.gender2].filter(Boolean).join(' '),
    ]
        .filter(Boolean)
        .join('; then ');

    return [
        row('Name', name),
        row('City', location),
        row('Preferences', prefs),
        row('Open to other prefs', fields.flexPrefs),
        row('Older puppy / adult', fields.olderDog),
        row('First dog', fields.firstDog),
        row('First puppy', fields.firstPuppy),
        row('Other pets', fields.otherPets || fields.otherPetsYes),
        row('Grooming', fields.grooming),
        row('Plans', fields.plans),
        row('Housing', [fields.housingType, fields.ownRent, fields.housingOther].filter(Boolean).join(' — ')),
        row('Yard', [fields.yard, fields.fence].filter(Boolean).join(', ')),
        row('Landlord', fields.landlord),
        row('Adults agree', fields.adultsAgree),
        row('Household ages', fields.ages),
        row('Allergies', fields.allergies),
        row('Time spent', fields.whereTime),
        row('Training', fields.training),
        row('Crate', fields.crate),
        row('Indoor / outdoor', fields.indoor),
        row('Hours alone', fields.hoursAlone),
        row('Gift', fields.gift),
        row('How soon', fields.howSoon),
        row('Pickup age', fields.pickupAge),
        row('Austin pickup', fields.austin),
        row('Commitment', fields.commitment),
        row('Follow-up', fields.followUp),
        row('Rehome through us', fields.rehome),
        row('Why', fields.why),
        row('Heard about us', fields.heard),
        row('Other info', fields.otherInfo),
        row('Questions', fields.questions),
    ].filter((item): item is ApplicationFieldRow => Boolean(item));
};

export const formatApplicationSummary = (fields: Record<string, string>): string =>
    applicationFieldRows(fields)
        .map((item) => `${item.label}: ${item.value}`)
        .join('\n');

export const buildApplicationSummary = (text: string): ExtractedApplication => {
    const fields = extractApplicationFields(text);
    const rows = applicationFieldRows(fields);
    return {
        phone: fields.phone || '',
        summary: rows.map((item) => `${item.label}: ${item.value}`).join('\n'),
        fields: rows,
    };
};
